import { useState, useEffect } from "react";
import axios from "axios";
import { Contact, Plus, Search, Loader2, Trash2, Building, Globe, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FALLBACK_KNOWLEDGE_CONTACTS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CONTINENTS = ["Africa", "Europe", "Asia", "North America", "South America", "Oceania"];

const COUNTRIES_BY_CONTINENT = {
  Africa: ["South Africa", "Nigeria", "Kenya", "Egypt", "Ghana", "Ethiopia", "Tanzania", "Botswana", "Zimbabwe", "Mozambique", "Angola", "Namibia", "Zambia", "DRC", "Morocco", "Algeria", "Sudan", "Uganda", "Senegal", "Rwanda"],
  Europe: ["UK", "Germany", "France", "Italy", "Spain", "Netherlands", "Switzerland", "Sweden", "Norway", "Denmark", "Finland", "Belgium", "Portugal", "Poland", "Ireland", "Austria", "Greece", "Russia", "Turkey", "Ukraine"],
  Asia: ["China", "India", "Japan", "South Korea", "Singapore", "UAE", "Saudi Arabia", "Qatar", "Israel", "Malaysia", "Indonesia", "Pakistan", "Bangladesh", "Vietnam", "Thailand", "Philippines", "Kuwait", "Oman", "Jordan", "Sri Lanka"],
  "North America": ["USA", "Canada", "Mexico", "Panama", "Costa Rica", "Jamaica", "Trinidad and Tobago"],
  "South America": ["Brazil", "Argentina", "Chile", "Colombia", "Peru", "Venezuela", "Ecuador", "Uruguay", "Paraguay", "Bolivia"],
  Oceania: ["Australia", "New Zealand", "Fiji", "Papua New Guinea", "Solomon Islands"],
};

export default function KbContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterContinent, setFilterContinent] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formRole, setFormRole] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formDepartment, setFormDepartment] = useState("");
  const [formType, setFormType] = useState("internal");
  const [formContinent, setFormContinent] = useState("");
  const [formCountry, setFormCountry] = useState("");
  const [formNotes, setFormNotes] = useState("");

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterContinent) params.continent = filterContinent;
      if (filterCountry) params.country = filterCountry;
      if (filterDepartment) params.department = filterDepartment;

      const res = await axios.get(`${API}/knowledge/contacts`, {
        params,
        headers: getHeaders(),
      });
      setContacts(res.data);
    } catch {
      setContacts(FALLBACK_KNOWLEDGE_CONTACTS);
      toast.error("Failed to load contacts. Showing demo data.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContacts();
  }, [filterType, filterContinent, filterCountry, filterDepartment]);

  const handleCreate = async () => {
    if (!formName.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      await axios.post(
        `${API}/knowledge/contacts`,
        {
          name: formName.trim(),
          role: formRole,
          email: formEmail,
          phone: formPhone,
          department: formDepartment,
          type: formType,
          continent: formContinent,
          country: formCountry,
          notes: formNotes,
        },
        { headers: getHeaders() }
      );
      toast.success("Contact created");
      setDialogOpen(false);
      resetForm();
      fetchContacts();
    } catch {
      toast.error("Failed to create contact");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/knowledge/contacts/${id}`, { headers: getHeaders() });
      toast.success("Contact deleted");
      fetchContacts();
    } catch {
      toast.error("Failed to delete contact");
    }
  };

  const resetForm = () => {
    setFormName("");
    setFormRole("");
    setFormEmail("");
    setFormPhone("");
    setFormDepartment("");
    setFormType("internal");
    setFormContinent("");
    setFormCountry("");
    setFormNotes("");
  };

  const filtered = contacts.filter((c) =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.role?.toLowerCase().includes(search.toLowerCase()) ||
    c.department?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
            <Contact className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-app tracking-wide">
              CONTACTS
            </h1>
            <p className="text-app">Internal & external contacts directory</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="internal" onValueChange={(v) => setFilterType(v)} className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="rounded-none border border-app">
            <TabsTrigger value="internal" className="rounded-none data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              <Building className="w-4 h-4 mr-2" /> Internal
            </TabsTrigger>
            <TabsTrigger value="external" className="rounded-none data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              <Globe className="w-4 h-4 mr-2" /> External
            </TabsTrigger>
          </TabsList>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 rounded-none text-white">
                <Plus className="w-4 h-4 mr-2" /> Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-app-card border-app rounded-none sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] text-xl">Add Contact</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <Input placeholder="Name *" value={formName} onChange={(e) => setFormName(e.target.value)} className="rounded-none bg-app border-app" />
                <Input placeholder="Role / Title" value={formRole} onChange={(e) => setFormRole(e.target.value)} className="rounded-none bg-app border-app" />
                <Input placeholder="Email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} className="rounded-none bg-app border-app" />
                <Input placeholder="Phone" value={formPhone} onChange={(e) => setFormPhone(e.target.value)} className="rounded-none bg-app border-app" />
                <Input placeholder="Department" value={formDepartment} onChange={(e) => setFormDepartment(e.target.value)} className="rounded-none bg-app border-app" />

                <Select value={formType} onValueChange={setFormType}>
                  <SelectTrigger className="rounded-none bg-app border-app">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-app-card border-app rounded-none">
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                  </SelectContent>
                </Select>

                {formType === "external" && (
                  <>
                    <Select value={formContinent} onValueChange={setFormContinent}>
                      <SelectTrigger className="rounded-none bg-app border-app">
                        <SelectValue placeholder="Select continent" />
                      </SelectTrigger>
                      <SelectContent className="bg-app-card border-app rounded-none">
                        {CONTINENTS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formContinent && (
                      <Select value={formCountry} onValueChange={setFormCountry}>
                        <SelectTrigger className="rounded-none bg-app border-app">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent className="bg-app-card border-app rounded-none max-h-60">
                          {(COUNTRIES_BY_CONTINENT[formContinent] || []).map((co) => (
                            <SelectItem key={co} value={co}>{co}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </>
                )}

                <textarea
                  placeholder="Notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-app bg-app rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
                  rows={3}
                />

                <Button onClick={handleCreate} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none">
                  <Plus className="w-4 h-4 mr-2" /> Create Contact
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="bg-app-card border border-app p-4 mb-6 flex items-center gap-3 flex-wrap">
          <Search className="w-4 h-4 text-app-secondary" />
          <Input
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48 bg-app border-app rounded-none"
          />
          <div className="w-px h-6 bg-slate-200" />

          {filterType === "external" && (
            <>
              <Select value={filterContinent} onValueChange={setFilterContinent}>
                <SelectTrigger className="w-40 bg-app border-app rounded-none">
                  <SelectValue placeholder="All Continents" />
                </SelectTrigger>
                <SelectContent className="bg-app-card border-app rounded-none">
                  <SelectItem value="all">All Continents</SelectItem>
                  {CONTINENTS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {filterContinent && filterContinent !== "all" && (
                <Select value={filterCountry} onValueChange={setFilterCountry}>
                  <SelectTrigger className="w-40 bg-app border-app rounded-none">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent className="bg-app-card border-app rounded-none">
                    <SelectItem value="all">All Countries</SelectItem>
                    {(COUNTRIES_BY_CONTINENT[filterContinent] || []).map((co) => (
                      <SelectItem key={co} value={co}>{co}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}

          <Button onClick={fetchContacts} size="sm" className="rounded-none bg-cyan-600 hover:bg-cyan-500">
            <Search className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </Tabs>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-app-card border border-dashed border-app p-12 text-center">
          <Contact className="w-12 h-12 text-app-secondary mx-auto mb-3" />
          <p className="text-app-secondary">No contacts found.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} className="bg-app-card border border-app p-5 hover:border-cyan-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 bg-cyan-50 text-cyan-600 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="text-app-secondary hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <h3 className="font-semibold text-app">{c.name}</h3>
              {c.role && <p className="text-sm text-app-secondary">{c.role}</p>}
              {c.department && (
                <p className="text-xs text-app-secondary mt-1">
                  <Building className="w-3 h-3 inline mr-1" />
                  {c.department}
                </p>
              )}

              <div className="mt-3 space-y-1">
                {c.email && (
                  <a href={`mailto:${c.email}`} className="block text-xs text-cyan-600 hover:underline">
                    {c.email}
                  </a>
                )}
                {c.phone && (
                  <p className="text-xs text-app-secondary">{c.phone}</p>
                )}
              </div>

              {c.type === "external" && c.continent && (
                <div className="mt-3 pt-3 border-t border-app">
                  <p className="text-xs text-app-secondary">
                    <Globe className="w-3 h-3 inline mr-1" />
                    {c.continent}{c.country ? ` / ${c.country}` : ""}
                  </p>
                </div>
              )}

              {c.notes && (
                <p className="text-xs text-app-secondary mt-2 italic line-clamp-2">{c.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
